// Casos de Servicio — mesa de servicio GLOBAL del administrador del sistema.
//
// Muestra TODOS los casos de soporte/feedback de TODAS las cuentas, con su
// detalle completo, y permite CERRAR un caso abierto una vez respondido
// manualmente por correo. Filtrable por cuenta y por estado. Consume
// `GET /admin/support`, `GET /admin/support/{id}` y `PATCH /admin/support/{id}`.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import type { AdminSupportTicket } from '../api/endpoints.js';
import { AlertMessage, AsyncBoundary, Badge, ButtonSpinner, Card, Icon, PageHeader } from '../ui/components.js';
import { useAsync } from '../ui/useAsync.js';

type Status = 'open' | 'in_progress' | 'resolved' | 'closed';

/** Tono del badge por estado (la etiqueta se resuelve vía i18n). */
const STATUS_TONE: Record<Status, 'warn' | 'info' | 'success' | 'neutral'> = {
  open: 'warn',
  in_progress: 'info',
  resolved: 'success',
  closed: 'neutral',
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export function ServiceCasesView() {
  const { t } = useTranslation();
  const api = useApi();
  const [statusFilter, setStatusFilter] = useState<'' | Status>('');
  const cases = useAsync(
    () => api.admin.listSupport(statusFilter ? { status: statusFilter } : {}),
    [statusFilter],
  );
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('serviceCases.pageTitle')}
        subtitle={t('serviceCases.pageSubtitle')}
        back
      />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <AlertMessage message={error} />
        {message && (
          <div className="p-space-sm rounded-lg bg-goldmine-mint/15 border border-goldmine-mint/40 text-goldmine-mint font-body-sm text-body-sm">
            {message}
          </div>
        )}

        {/* Filtro por estado. */}
        <div className="field-slot">
          <Icon name="filter_list" className="text-on-surface-variant text-[18px] mr-space-xs" />
          <select
            className="field-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as '' | Status)}
            aria-label={t('serviceCases.filterByStatus')}
          >
            <option value="">{t('serviceCases.allStatuses')}</option>
            <option value="open">{t('serviceCases.statusOpenPlural')}</option>
            <option value="in_progress">{t('serviceCases.statusInProgressPlural')}</option>
            <option value="resolved">{t('serviceCases.statusResolvedPlural')}</option>
            <option value="closed">{t('serviceCases.statusClosedPlural')}</option>
          </select>
        </div>

        <AsyncBoundary state={cases}>
          {(data) => (
            <CaseList
              cases={data.items}
              onError={setError}
              onMessage={setMessage}
              onChanged={() => cases.reload()}
            />
          )}
        </AsyncBoundary>
      </div>
    </div>
  );
}

function CaseList({
  cases,
  onError,
  onMessage,
  onChanged,
}: {
  cases: AdminSupportTicket[];
  onError: (m: string | null) => void;
  onMessage: (m: string | null) => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const openCount = useMemo(() => cases.filter((c) => c.status === 'open').length, [cases]);

  if (cases.length === 0) {
    return <p className="font-body-sm text-body-sm text-on-surface-variant">{t('serviceCases.noCases')}</p>;
  }
  return (
    <div className="flex flex-col gap-space-sm">
      <span className="font-code-catalog text-body-sm text-on-surface-variant">
        {t('serviceCases.summary', { count: cases.length, open: openCount })}
      </span>
      {cases.map((c) => (
        <CaseCard key={c.id} ticket={c} onError={onError} onMessage={onMessage} onChanged={onChanged} />
      ))}
    </div>
  );
}

function CaseCard({
  ticket,
  onError,
  onMessage,
  onChanged,
}: {
  ticket: AdminSupportTicket;
  onError: (m: string | null) => void;
  onMessage: (m: string | null) => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const status = (ticket.status as Status) in STATUS_TONE ? (ticket.status as Status) : 'open';
  const tone = STATUS_TONE[status];
  const canClose = ticket.status !== 'closed';

  async function close() {
    if (!confirm(t('serviceCases.closeConfirm', { number: ticket.case_number, subject: ticket.subject }))) return;
    setBusy(true);
    onError(null);
    onMessage(null);
    try {
      await api.admin.setSupportStatus(ticket.id, 'closed');
      onMessage(t('serviceCases.caseClosed', { number: ticket.case_number }));
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : t('serviceCases.errClose'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title={`#${ticket.case_number} · ${ticket.subject}`}
      icon={ticket.type === 'feedback' ? 'lightbulb' : 'confirmation_number'}
      action={<Badge tone={tone}>{t(`serviceCases.status.${status}`)}</Badge>}
    >
      <div className="flex flex-col gap-space-xs font-body-sm text-body-sm text-on-surface-variant">
        <span className="flex items-center gap-space-xs">
          <Icon name="library_music" className="text-[16px]" /> {ticket.tenant_name ?? ticket.tenant_id}
        </span>
        <span className="flex items-center gap-space-xs">
          <Icon name="label" className="text-[16px]" /> {ticket.category_label}
          {' · '}
          {ticket.type === 'feedback' ? t('serviceCases.typeRecommendation') : t('serviceCases.typeHelp')}
        </span>
        <span className="flex items-center gap-space-xs">
          <Icon name="schedule" className="text-[16px]" /> {fmtDate(ticket.created_at)}
        </span>
      </div>

      <button
        type="button"
        className="mt-space-sm flex items-center gap-space-xs text-primary font-body-sm text-body-sm"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <Icon name={open ? 'expand_less' : 'expand_more'} className="text-[18px]" />
        {open ? t('serviceCases.hideDetail') : t('serviceCases.viewDetail')}
      </button>

      {open && (
        <div className="mt-space-sm flex flex-col gap-space-sm">
          <div className="surface-rack-card !p-space-sm">
            <p className="section-label text-on-surface-variant mb-space-xs">{t('serviceCases.message')}</p>
            <p className="font-body-md text-body-md text-on-surface whitespace-pre-wrap break-words">
              {ticket.message}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-space-xs font-body-sm text-body-sm">
            <Fact icon="person" label={t('serviceCases.contact')} value={ticket.contact_name ?? '—'} />
            <Fact
              icon="mail"
              label={t('serviceCases.email')}
              value={ticket.contact_email ?? ticket.submitter_email ?? '—'}
              href={ticket.contact_email ?? ticket.submitter_email ?? undefined}
            />
            <Fact icon="update" label={t('serviceCases.updated')} value={fmtDate(ticket.updated_at)} />
            <Fact icon="outgoing_mail" label={t('serviceCases.notifiedAdmin')} value={ticket.email_sent ? t('serviceCases.yes') : t('serviceCases.no')} />
          </div>
        </div>
      )}

      {canClose && (
        <button className="btn-secondary btn-sm self-start mt-space-sm" type="button" onClick={() => void close()} disabled={busy}>
          {busy ? <ButtonSpinner /> : <Icon name="task_alt" className="text-[16px]" />} {t('serviceCases.closeCase')}
        </button>
      )}
    </Card>
  );
}

function Fact({ icon, label, value, href }: { icon: string; label: string; value: string; href?: string }) {
  return (
    <span className="flex items-center gap-space-xs min-w-0">
      <Icon name={icon} className="text-on-surface-variant text-[16px] shrink-0" />
      <span className="text-on-surface-variant">{label}:</span>
      {href ? (
        <a className="text-primary truncate" href={`mailto:${href}`}>
          {value}
        </a>
      ) : (
        <span className="text-on-surface truncate" title={value}>
          {value}
        </span>
      )}
    </span>
  );
}
